import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import {
  subscribeToConversations,
  getOrCreateFamilyChat,
  getConversation
} from '../../services/messagingService';
import ConversationList from './ConversationList';
import ConversationView from './ConversationView';
import NewMessageDialog from './NewMessageDialog';
import logger from '../../utils/logger';

/**
 * Main Messages page — sidebar (conversations) + content area (active conversation).
 *
 * Desktop: side-by-side layout
 * Mobile: shows either list OR conversation (with back button)
 *
 * Routes:
 *   /messages             → conversation list (no active conversation)
 *   /messages/:convId     → specific conversation open
 */
const MessagesPage = () => {
  const { conversationId: routeConvId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { currentUser } = useAuth();
  const { profile } = useProfile();

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [memberMap, setMemberMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [newMessageOpen, setNewMessageOpen] = useState(false);

  const familyCode = profile?.familyCode;

  // Load family member map (for displaying names in conversations)
  useEffect(() => {
    if (!familyCode) return;

    const loadMembers = async () => {
      try {
        const membersRef = collection(db, 'families', familyCode, 'members');
        const snapshot = await getDocs(membersRef);
        const map = {};
        snapshot.forEach((doc) => {
          const data = doc.data();
          map[doc.id] = {
            displayName: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            name: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            photoURL: data.photoURL || null,
            email: data.email
          };
        });
        setMemberMap(map);
      } catch (err) {
        logger.error('Error loading member map:', err);
      }
    };

    loadMembers();
  }, [familyCode]);

  // Ensure family chat exists
  useEffect(() => {
    if (!familyCode || !currentUser?.uid || Object.keys(memberMap).length === 0) return;

    const allMemberIds = Object.keys(memberMap);
    const userName = profile?.displayName
      || `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
      || currentUser.displayName
      || 'User';

    getOrCreateFamilyChat(familyCode, allMemberIds, currentUser.uid, userName)
      .catch((err) => logger.warn('Error ensuring family chat:', err));
  }, [familyCode, currentUser?.uid, currentUser?.displayName, memberMap, profile]);

  // Subscribe to conversations (real-time)
  useEffect(() => {
    if (!familyCode || !currentUser?.uid) return;

    setLoading(true);
    const unsubscribe = subscribeToConversations(familyCode, currentUser.uid, (convs) => {
      setConversations(convs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [familyCode, currentUser?.uid]);

  // Load active conversation from route param
  useEffect(() => {
    if (!routeConvId || !familyCode) {
      setActiveConversation(null);
      return;
    }

    // First check if it's in the loaded conversations
    const found = conversations.find(c => c.id === routeConvId);
    if (found) {
      setActiveConversation(found);
      return;
    }

    // Otherwise fetch it directly
    const loadConversation = async () => {
      try {
        const conv = await getConversation(familyCode, routeConvId);
        if (conv) {
          setActiveConversation(conv);
        }
      } catch (err) {
        logger.error('Error loading conversation:', err);
      }
    };

    loadConversation();
  }, [routeConvId, familyCode, conversations]);

  const handleSelectConversation = useCallback((conv) => {
    setActiveConversation(conv);
    navigate(`/messages/${conv.id}`, { replace: true });
  }, [navigate]);

  const handleBack = useCallback(() => {
    setActiveConversation(null);
    navigate('/messages', { replace: true });
  }, [navigate]);

  const handleNewConversation = useCallback((conversationId) => {
    navigate(`/messages/${conversationId}`, { replace: true });
  }, [navigate]);

  // No family code — show a message
  if (!familyCode) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Join a family to start messaging
        </Typography>
      </Box>
    );
  }

  // Mobile layout: show either list or conversation
  if (isMobile) {
    return (
      <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        {activeConversation ? (
          <ConversationView
            conversation={activeConversation}
            memberMap={memberMap}
            onBack={handleBack}
          />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
              <Typography variant="h5" fontWeight={600}>Messages</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewMessageOpen(true)}
              >
                New
              </Button>
            </Box>

            {/* Conversation list */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversation?.id}
                onSelect={handleSelectConversation}
                currentUserId={currentUser?.uid}
                memberMap={memberMap}
                familyCode={familyCode}
                loading={loading}
              />
            </Box>
          </Box>
        )}

        {/* FAB for new message (when in list view) */}
        {!activeConversation && (
          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 80, right: 16 }}
            onClick={() => setNewMessageOpen(true)}
          >
            <AddIcon />
          </Fab>
        )}

        <NewMessageDialog
          open={newMessageOpen}
          onClose={() => setNewMessageOpen(false)}
          onConversationCreated={handleNewConversation}
          familyCode={familyCode}
        />
      </Box>
    );
  }

  // Desktop layout: sidebar + content area
  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', gap: 0 }}>
      {/* Sidebar */}
      <Paper
        elevation={2}
        sx={{
          width: 360,
          minWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider'
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>Messages</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setNewMessageOpen(true)}
          >
            New Message
          </Button>
        </Box>

        {/* Conversation list */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <ConversationList
            conversations={conversations}
            activeConversationId={activeConversation?.id}
            onSelect={handleSelectConversation}
            currentUserId={currentUser?.uid}
            memberMap={memberMap}
            familyCode={familyCode}
            loading={loading}
          />
        </Box>
      </Paper>

      {/* Content area */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0
        }}
      >
        <ConversationView
          conversation={activeConversation}
          memberMap={memberMap}
        />
      </Paper>

      <NewMessageDialog
        open={newMessageOpen}
        onClose={() => setNewMessageOpen(false)}
        onConversationCreated={handleNewConversation}
        familyCode={familyCode}
      />
    </Box>
  );
};

export default MessagesPage;
